import { useEffect, useState } from 'react';

export default function TelemetryDashboard({ stats, onRestart, isVictory, isSaving = false, restartHint = '' }) {
  const [displayed, setDisplayed] = useState({ finalScore: 0 });
  const [phase, setPhase] = useState(0);
  const [isRestarting, setIsRestarting] = useState(false);

  useEffect(() => {
    setDisplayed({ finalScore: 0 });
    setPhase(0);
    setIsRestarting(false);
  }, [stats]);

  useEffect(() => {
    let animId;
    let startTime;
    const duration = 1500;
    let phaseTimeoutId;
    let revealTimeoutId;

    const animateValue = (start, end, setter, onComplete) => {
      const step = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        const easeOutQuad = 1 - (1 - progress) * (1 - progress);
        const current = Math.floor(start + (end - start) * easeOutQuad);
        setter(current);
        if (progress < 1) {
          animId = requestAnimationFrame(step);
        } else {
          setter(end);
          if (onComplete) onComplete();
        }
      };
      animId = requestAnimationFrame(step);
    };

    if (phase === 0) {
      phaseTimeoutId = window.setTimeout(() => setPhase(1), 500);
    } else if (phase === 1) {
      startTime = null;
      animateValue(0, stats.score, (value) => setDisplayed({ finalScore: value }), () => {
        revealTimeoutId = window.setTimeout(() => setPhase(2), 300);
      });
    }

    return () => {
      cancelAnimationFrame(animId);
      window.clearTimeout(phaseTimeoutId);
      window.clearTimeout(revealTimeoutId);
    };
  }, [phase, stats]);

  const handleRestart = async () => {
    if (isRestarting || isSaving) return;
    setIsRestarting(true);

    try {
      await onRestart?.();
    } catch (error) {
      console.error('No se pudo reiniciar la simulacion.', error);
      setIsRestarting(false);
    }
  };

  return (
    <div className="relative m-auto mx-4 w-full shrink-0 max-w-xl overflow-hidden border border-[#7EC54A]/60 bg-[#0F1F12]/95 p-6 text-center shadow-[10px_12px_0_rgba(0,0,0,.55)] md:p-8">
      <div className="absolute inset-1 z-0 border border-[#F7E04B]/15 pointer-events-none" />
      <div className="relative z-10">
        <p className="text-[10px] font-black uppercase tracking-[.24em] text-[#F7E04B]">Registro de la noche</p>
        <h1 className={`font-display mb-2 mt-2 text-5xl uppercase leading-none md:text-6xl ${isVictory ? 'text-[#F7E04B]' : 'text-[#7EC54A]'}`}>
          {isVictory ? 'Ritual completo' : 'El pulso cayó'}
        </h1>
        <p className="mb-6 text-xs uppercase tracking-[.16em] text-[#C5E6A0]">
          {isVictory ? 'Escapaste de la maldición' : 'El Diablito te alcanzó'}
        </p>

        <div className="mb-6 border-y border-[#7EC54A]/35 py-5">
          <div className="mb-5 border-b border-[#F7E04B]/25 pb-5">
            <p className="mb-2 text-[10px] font-black uppercase tracking-[.2em] text-[#7EC54A]">Puntaje de la noche</p>
            <p className="font-display text-7xl leading-none text-[#F7E04B] drop-shadow-[3px_3px_0_#1A4D2E]">
              {displayed.finalScore.toLocaleString()}
            </p>
          </div>

          <div className={`grid grid-cols-2 gap-px bg-[#7EC54A]/30 transition-all duration-700 ${phase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="bg-[#0F1F12] p-3 text-left">
              <p className="text-[9px] font-black uppercase tracking-wider text-[#7EC54A]">Precisión</p>
              <p className="font-display text-2xl text-white">{stats.accuracy ?? 0}%</p>
            </div>
            <div className="bg-[#0F1F12] p-3 text-left">
              <p className="text-[9px] font-black uppercase tracking-wider text-[#7EC54A]">Mejor combo</p>
              <p className="font-display text-2xl text-white">{stats.maxCombo ?? 0}</p>
            </div>
            <div className="bg-[#0F1F12] p-3 text-left">
              <p className="text-[9px] font-black uppercase tracking-wider text-[#7EC54A]">Tiempo</p>
              <p className="font-display text-2xl text-white">{Math.floor(stats.time / 60)}:{(stats.time % 60).toString().padStart(2, '0')}</p>
            </div>
            <div className="bg-[#0F1F12] p-3 text-left">
              <p className="text-[9px] font-black uppercase tracking-wider text-[#7EC54A]">Racha máxima</p>
              <p className="font-display text-2xl text-white">{Math.floor(stats.maxStreakTime)}s</p>
            </div>
            <div className="bg-[#0F1F12] p-3 text-left">
              <p className="text-[9px] font-black uppercase tracking-wider text-[#7EC54A]">Aciertos</p>
              <p className="font-display text-2xl text-[#7EC54A]">{stats.totalHits}</p>
            </div>
            <div className="bg-[#0F1F12] p-3 text-left">
              <p className="text-[9px] font-black uppercase tracking-wider text-[#7EC54A]">Fallos</p>
              <p className="font-display text-2xl text-[#F7E04B]">{stats.totalMisses}</p>
            </div>
          </div>
        </div>

        {restartHint ? (
          <p className="border border-[#F7E04B]/45 bg-black/40 px-4 py-4 text-xs font-bold uppercase tracking-wider text-[#F7E04B]" role="status">
            {restartHint}
          </p>
        ) : (
          <button
            type="button"
            onClick={handleRestart}
            disabled={isRestarting || isSaving}
            className={`w-full border border-[#F7E04B] px-6 py-4 font-black uppercase tracking-[.14em] text-white shadow-[4px_4px_0_#0A1A0F] transition-transform active:translate-x-0.5 active:translate-y-0.5 ${isRestarting ? 'cursor-wait bg-[#1A4D2E] opacity-80' : 'bg-[#2E7D32] hover:bg-[#3A9D23]'}`}
          >
            {isSaving ? 'Guardando puntaje…' : isRestarting ? 'Abriendo el ritual...' : 'Intentarlo de nuevo'}
          </button>
        )}
      </div>
    </div>
  );
}
